import { PAGE_TYPE_ENUM } from "@shared/types";
import BlogPageContent from "components/blog/BlogPageContent";
import PageLayout from "components/layout/PageLayout";
import { setDYContext } from "config";
import { useLogContext } from "context/log";
import { generatePageId } from "helpers/analyticsLogger";
import { setCachingHeader } from "helpers/cacheHeader";
import { GetServerSideProps } from "next";
import { useEffect } from "react";
import { createDatoClient } from "services/datocms";
import {
  AllBlogSectionsQuery,
  ArticlesBySectionQuery,
  BlogPageQuery,
  getSdk,
} from "services/datocms/generated";
import { createQueryParamsForCaching } from "utils/create-query-params";

type CategoryPageProps = {
  sections: AllBlogSectionsQuery["allBlogSections"];
  category?: string;
  pageData?: BlogPageQuery["blogPage"] | null;
  articles?: ArticlesBySectionQuery["allArticles"];
};

export const getServerSideProps: GetServerSideProps<CategoryPageProps> = async (
  context,
) => {
  setCachingHeader(context);

  const category = context.params?.category as string;
  try {
    const { allBlogSections } = await getSdk(
      createDatoClient(
        context.preview,
        createQueryParamsForCaching({
          queryType: "AllBlogSections",
        }),
      ),
    ).AllBlogSections();
    const selectedSection = allBlogSections.find(
      (section) => section.slug === category,
    );

    let articles;
    if (selectedSection) {
      const { allArticles } = await getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "ArticlesBySection",
            queryVariables: { sectionId: selectedSection.id },
          }),
        ),
      ).ArticlesBySection({ sectionId: selectedSection.id });
      articles = allArticles;
    } else {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        sections: allBlogSections ?? [],
        articles: articles,
        category: category,
      },
    };
  } catch (error) {
    return {
      props: {
        sections: [],
        category: category,
      },
    };
  }
};

const CategoryPage: React.FC<CategoryPageProps> = ({
  sections,
  pageData,
  category,
  articles,
}) => {
  const { useLogPageView } = useLogContext();

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.blog,
    id: generatePageId(`blog category ${category}`),
  });

  useEffect(() => {
    category && setDYContext("CATEGORY", [category]);
  }, [category]);

  const selectedSection = sections.find((section) => section.slug === category);

  return (
    <PageLayout seo={selectedSection?.seo} navigationOverlapsContent>
      <BlogPageContent
        sections={sections}
        selectedSection={selectedSection}
        pageData={pageData}
        articles={articles}
      />
    </PageLayout>
  );
};

export default CategoryPage;
